import api from 'api';
import Privileges from '../lib/constants/privileges';
import colors from 'lib/constants/resourceColors';
import invoiceBadgeStyle from 'lib/helpers/invoiceBadgeStyle';

const form = [
    {key: 'transaction_number', label: 'Transaction #', static: true, readOnly: true},
    {key: 'customer_id',
     displayKey: ['customer_name'],
        label: 'Customer',
        createOnly: true,
        type: 'autocomplete',
        link: { url: '/customers/:customer_id', privileges: Privileges.READ_CUSTOMERS },
        suggestFormat: (c) => {
            let text = `${c.name}`;
            return {value: c.id, text}
        },
        suggestUrl: '/customers',
        suggestFields: ['id', 'name']},
    {type: 'currency',
        key: 'amount',
        required: true,
        // Set on create; only editable afterward by users holding write_transactions_amount
        // (typo correction). Backend enforces the same privilege on the update payload.
        updatePrivileges: [Privileges.WRITE_TRANSACTIONS_AMOUNT]},
    {key: 'type',
        label: 'Payment Type',
        type: 'options',
        required: true,
        defaultValue: 'check',
        inputRef: 'type',
        options: [
            {value: 'check',       label: 'Check'},
            {value: 'credit_card', label: 'Credit Card'},
            {value: 'ach',         label: 'ACH'},
            {value: 'wire',        label: 'Wire'},
        ]},
    {key: 'check_number', condition: {key: 'type', value: 'check'}},
    {key: 'last_digits', label: 'Last 4 CC Digits', condition: {key: 'type', value: 'credit_card'}},
    {key: 'reference_number', label: 'Confirmation #', condition: {key: 'type', values: ['ach', 'wire']}},
    // User-settable payment date (defaults to today at the DB layer, migration 1785300200).
    // Distinct from `created_at` (system insert time) so back-/post-dated payments read true.
    {key: 'payment_date', label: 'Payment Date', type: 'date'},
    'memo',
    {label: 'Payments Credited', key: 'payments_sum', static: true, readOnly: true, type: 'currency'},
    {label: 'Remaining Credit', key: 'available_credit', static: true, readOnly: true, type: 'currency'},
    '^file(File){file_url}',
    // Read-only: voiding must go through the "Void Payment" action so it cascades onto the
    // applied invoice_payments. A raw checkbox edit would flip only transactions.voided and
    // leave the applications showing as live.
    {key: 'voided', label: 'Voided', static: true, readOnly: true, type: 'boolean'},
    {key: 'transaction_invoices',
        type: 'references',
        heading: 'Applied to Invoices',
        readPrivileges: [Privileges.LIST_TRANSACTION_INVOICES],
        dataUrl: '/lists/transaction_invoices?transaction_id=:id',
        tableProps: {
            rowURL: '/invoices/:invoice_id',
            columns: [
                {heading: 'Payment Date', accessor: 'payment_date', type: 'date', dateFormat: 'MMM DD, YYYY', cellWidth: '110px'},
                {heading: 'Invoice #', accessor: 'invoice_number', cellWidth: '110px', style: 'bold'},
                {heading: 'Description', accessor: 'invoice_description'},
                {heading: 'Amount Applied', type: 'currency', accessor: 'amount'},
                {heading: 'Open Balance', type: 'currency', accessor: 'open_balance', cellWidth: '120px'},
                {heading: 'Invoice Status', type: 'badge', accessor: 'invoice_status', badgePropsFunc: invoiceBadgeStyle},
            ]
        }
    },
    // Open invoices for this payment's customer — what this payment / its available credit
    // could be applied to. Scoped by the record's customer_id. rowURL makes each row a link
    // into the invoice detail screen. The list itself already excludes voided invoices and
    // any with open_balance = 0 (honors the void flag via invoices_extended.open_balance).
    {key: 'customer_open_invoices',
        type: 'references',
        heading: 'Open Invoices',
        readPrivileges: [Privileges.LIST_CUSTOMER_OPEN_INVOICES],
        dataUrl: '/lists/customer_open_invoices?customer_id=:customer_id',
        tableProps: {
            rowURL: '/invoices/:id',
            columns: [
                {heading: 'Date', accessor: 'date', type: 'date', dateFormat: 'MMM DD, YYYY', cellWidth: '100px'},
                {heading: 'Invoice #', accessor: 'invoice_number', cellWidth: '110px', style: 'bold'},
                {heading: 'Description', accessor: 'project_description'},
                {heading: 'Grand Total', type: 'currency', accessor: 'grand_total', cellWidth: '120px'},
                {heading: 'Open Balance', type: 'currency', accessor: 'open_balance', cellWidth: '120px'},
                {heading: 'Status', type: 'badge', accessor: 'invoice_status', badgePropsFunc: invoiceBadgeStyle},
            ]
        }
    },
]

export default {
    defaults: {
        name: 'Transaction',
        color: colors.transaction,
        //identifierParam: 'transaction_number',
        formFields: form,
    },
    search: {
        allowDateFilter: true,
        heading: 'Payments',
        searchBy: ['transaction number', 'customer name', 'customer number', 'check number', 'memo', 'amount', 'last four credit card digits'],
        fields: ['id','transaction_number', 'amount','customer_name', 'voided', 'payment_date', 'created_at','updated_at'],
        // Default view hides voided payments; the radio must be first AND flagged default so the
        // initial query (filterOptions[0]) and the highlighted radio (default flag) agree.
        // Standalone/courtesy credits (Accounting -> Credits) live in this same table marked
        // is_credit=TRUE; keep them out of the Payments (deposited-money) list. "All" still shows
        // every real payment (voided included) but not courtesy credits.
        filterOptions: [
            {label: 'Payments', filters: {voided: false, is_credit: false}, default: true},
            {label: 'Voided', filters: {voided: true, is_credit: false}},
            {label: 'All', filters: {is_credit: false}},
        ],
        links: [
            {url: '/transactions/new', icon: 'plus', title: 'New Payment', requiredPrivileges: [Privileges.CREATE_TRANSACTIONS]},
            // Deposit Slip PDF for the current date filter. Always excludes voided payments
            // (backend enforces voided = false regardless of the voided radio). Opens inline.
            {downloadURL: (search_state) => {
                let params = '';
                if(search_state.search_by_date) {
                    let queries = [];
                    if(search_state.start_date) {
                        queries.push(`start_date=${search_state.start_date}`);
                    }
                    if(search_state.end_date) {
                        queries.push(`end_date=${search_state.end_date}`);
                    }
                    if(search_state.selected_date_range_type) {
                        queries.push(`date_range_type=${search_state.selected_date_range_type}`);
                    }
                    params = queries.length ? `?${queries.join('&')}` : '';
                }
                // Cache-buster: window.open on an identical URL re-serves the browser's
                // cached PDF, which can show a stale (pre-fix) copy. Force a fresh fetch.
                params += `${params ? '&' : '?'}_t=${Date.now()}`;
                return `/files/deposit_slip${params}`;
            },
                title: 'Deposit Slip',
                requiredPrivileges: [Privileges.FILE_READ_DEPOSIT_SLIP]
            },
        ],
        tableProps: {
            rowURL: '/transactions/:id',
            columns: [
                {heading: 'Payment Date', cellWidth: '110px', dateFormat: 'MMM DD, YYYY', type:'date', accessor: 'payment_date'},
                {cellWidth: '150px', accessor: 'customer_name'},
                {heading: 'Transaction #', accessor: 'transaction_number', cellWidth: '110px', style: 'bold'},
                {heading: 'Amount', accessor: 'amount', type: 'currency', cellWidth: '110px'},
                {heading: 'Voided', accessor: 'voided', type: 'boolean', cellWidth: '70px'},
            ]
        }
    },
    view: {
        title: () => 'Payment Details',
        formActions: [
            {   label: 'Void Payment',
                icon: 'ban',
                // Only offer Void on a live payment.
                visible: ({values}) => !values.voided,
                // Gated on the auto-seeded RPC_voidTransaction privilege (superuser bypasses).
                // Soft-void: flags the transaction voided AND cascades voided onto every
                // invoice_payment applied from it, which reopens those invoices and frees credit.
                privileges: [Privileges.VOID_TRANSACTION],
                confirm: {
                    title: 'Void Payment',
                    content: 'Void this payment? It will be marked voided and every invoice '
                           + 'payment applied from it will also be voided — the affected invoices '
                           + 'reopen and this payment stops counting as available credit.',
                    okText: 'Void Payment',
                    okType: 'danger',
                },
                method: async ({values, reload, loading, success, error}) => {
                    const transaction_id = values.id;

                    loading('Voiding');
                    let result;
                    try {
                        result = await api.rpc('voidTransaction', {transaction_id});
                    } catch (e) {
                        throw new Error(e);
                    }

                    if (result.success) {
                        const voidedCount = result.body && result.body.result
                                          && result.body.result.invoice_payments_voided;
                        success({
                            title: 'Payment Voided',
                            content: typeof voidedCount === 'number'
                                ? `This payment was voided, along with ${voidedCount} applied invoice payment(s).`
                                : 'This payment was voided.',
                            okText: 'Ok',
                        });
                    } else {
                        const rpcError = result.errors && result.errors.error;
                        error({
                            title: 'Could Not Void',
                            content: (rpcError && rpcError.message)
                                   || 'The payment could not be voided.',
                            okText: 'Ok',
                        });
                    }

                    reload(true);
                }
            },
            {   label: 'Unvoid Payment',
                icon: 'undo',
                // Only offer Unvoid on a voided payment.
                visible: ({values}) => !!values.voided,
                // Gated on the auto-seeded RPC_unvoidTransaction privilege (superuser bypasses).
                // Deliberately un-voids ONLY the transaction, NOT its applied invoice payments —
                // those stay voided, so the payment simply becomes available credit again to
                // re-apply deliberately.
                privileges: [Privileges.UNVOID_TRANSACTION],
                confirm: {
                    title: 'Unvoid Payment',
                    content: 'Unvoid this payment? It becomes active again and its amount counts '
                           + 'as available credit. The invoice payments that were voided with it '
                           + 'stay voided — re-apply the credit to invoices as needed.',
                    okText: 'Unvoid Payment',
                    okType: 'primary',
                },
                method: async ({values, reload, loading, success, error}) => {
                    const transaction_id = values.id;

                    loading('Unvoiding');
                    let result;
                    try {
                        result = await api.rpc('unvoidTransaction', {transaction_id});
                    } catch (e) {
                        throw new Error(e);
                    }

                    if (result.success) {
                        success({
                            title: 'Payment Unvoided',
                            content: 'This payment is active again. Its previously applied invoice '
                                   + 'payments remain voided; the amount is available as credit.',
                            okText: 'Ok',
                        });
                    } else {
                        const rpcError = result.errors && result.errors.error;
                        error({
                            title: 'Could Not Unvoid',
                            content: (rpcError && rpcError.message)
                                   || 'The payment could not be unvoided.',
                            okText: 'Ok',
                        });
                    }

                    reload(true);
                }
            },
        ],
        // Surfaces the History panel on the transaction detail screen. Backend already
        // captures every field edit (amount, memo, type, etc.) as an autogenerated
        // 'update' remark keyed on the transaction id via remarkUpdateHook; this block
        // is what makes those captured changes visible. object_type must match the
        // backend remarks config ('transaction').
        remarks: {
            header: (t) => t.transaction_number,
            object_type: 'transaction',
            object_id_key: 'id',
        },
    },
}
